import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { isEqual, isEqualWith, omitBy } from 'lodash';
import { CommonFunctionalitiesService } from '@app/services/common-functionalities.service';
import { ConfigBuilderService } from '@app/services/config-builder.service';
import { ConstantsService } from '@app/services/constants.service';
import { TourGuideContent } from '@app/util/tour-guide-content.util';
import { DynamicDropdownService } from '@app/services/dynamic-dropdown.service';
import _ from 'lodash';
import { startWith, map } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { Observable } from 'rxjs';
import { TourGuideService } from '@app/services/tour-guide.service';
import dummyData from '@app/assets/api-dummy-data.json';

/**interface for action dropdown */
export interface actionItem {
  id: number;
  name: string;
  show: boolean;
}

/**
 * This is a component class for trigger-and-artifact template
 */
@Component({
  selector: 'app-trigger-and-artifact',
  templateUrl: './trigger-and-artifact.component.html',
  styleUrls: ['./trigger-and-artifact.component.scss']
})
export class TriggerAndArtifactComponent implements OnInit {
  /******************************************* members variables *******************************************/
  private _artifactData: any;
  private _mode: 'read' | 'edit';

  triggerForm: FormGroup = this.fb.group({});
  tourGuidIds = TourGuideContent;
  organizations: any[] = [];
  githubEvents: any[] = [];
  actions: any[] = [];
  defaultSelectedAction = this.actions[0];
  filteredOrganizations: string[] = [];
  filteredEventOptions: Observable<any[]>;
  eventFilterControl = new FormControl();
  filterControl = new FormControl();
  actionItems: Array<actionItem>;
  filteredActions: Observable<any>;
  allSelected = false;
  /******************************************* members variables *******************************************/

  /************************************************ inputs ************************************************/
  @Input('isEditable') isEditable: boolean;
  @Input() wfSelected: any;

  /**
   * setter for _artifactData
   * @param data _artifactData
   */
  @Input('artifactData') set artifactData(data: any) {
    const actionIds = !this.commonService.isObjEmpty(data) && data.events ? data?.events[0]?.actions.map((action: any) => action.id) : [];
    this._artifactData = {
      repoOwner: data?.repoOwner || undefined,
      repoPtrn: data?.repoPtrn || undefined,
      branch: data?.brPtrn,
      event: !this.commonService.isObjEmpty(data) && data?.events[0]?.id || '',
      action: actionIds,
      artifact: {
        inputArtifact: data?.artifact?.inputArtifact,
        inputArtifactRepo: data?.artifact?.inputArtifactRepo ? this.commonService.appendSubString(data?.artifact.inputArtifactRepo) : '',
        outputArtifact: data?.artifact?.outputArtifact,
        outputArtifactRepo: data?.artifact?.outputArtifactRepo ? this.commonService.appendSubString(data?.artifact.outputArtifactRepo) : ''
      }
    }

    this.setInitialFrom();
  }

  /**
   * setter for _mode
   * @param mode read | edit
   */
  @Input() set mode(mode: 'read' | 'edit') {
    this._mode = mode;

    if (mode === 'edit') {
      this.triggerForm?.enable();
    } else {
      this.triggerForm?.disable();
    }
  };

  /************************************************ inputs ************************************************/


  /************************************************ outputs ************************************************/
  @Output() artifactOutput = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<boolean>();
  @Output() isChangeEvent = new EventEmitter<boolean>(false)
  @Output() changeEventValue = new EventEmitter<string>()
  /************************************************ outputs ************************************************/

  /***********View childs *************/
  @ViewChild('selectMult') select: MatSelect;

  /**
   * constructor
   * 
   * @param fb form builder
   * @param configBuilderService config builder service
   * @param constant constant service
   */
  constructor(
    private fb: FormBuilder,
    private configBuilderService: ConfigBuilderService,
    private constant: ConstantsService,
    private commonService: CommonFunctionalitiesService,
    private dpService: DynamicDropdownService,
    public guideService: TourGuideService
  ) {
    this.filterControl.setValue('');
  }

  /************************************************ getters ************************************************/
  /**
   * getter for trigger form value
   * @returns form value
   */
  get formValue() {
    let formValue = this.triggerForm.getRawValue();

    return {
      ...formValue,
      repoPtrn: this.trimGitHubPrefix(formValue.repoPtrn),
      branch: formValue.branch,
    };
  }

  // trim repoPtrn formValue if it contains https://github.com/
  trimGitHubPrefix(url: any) {
    let res: any;
    if (url.match(/(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z0-9]{2,}(\.[a-zA-Z0-9]{2,})(\.[a-zA-Z0-9]{2,})?/)) {
      let regex = url.match(/(?<repo>[^\/]+)\/?$/)
      res = '^' + regex.groups.repo + '$'
      return res
    } else {
      return url;
    }
  }

  /**
   * calculates the updated value by user in the form
   * 
   * @returns updated values
   */
  get changedFormValue() {
    let value = omitBy(this.formValue, (value, key) => isEqualWith(value, this._artifactData[key], this.customizer));

    let updatedActionsIds = {
      "added": _.difference(this.formValue.action, this.artifactData.action),
      "deleted": _.difference(this.artifactData.action, this.formValue.action)
    }
    value['eventIds'] = updatedActionsIds;
    return value;
  }

  /**
   * getter for _artifactData
   * @returns _artifactData
   */
  get artifactData() {
    return this._artifactData;
  }

  /**
   * getter for value change
   * @returns boolean
   */
  get isValueChanged() {
    return !isEqual(this.formValue, this.artifactData)
  }
  /************************************************ getters ************************************************/

  //custom method to treat null, (empty string) '' and undefined as false 
  customizer(objValue: any, othValue: any) {
    // Treat null, '', and undefined as false
    if ((objValue === null || objValue === '' || objValue === undefined) &&
      (othValue === null || othValue === '' || othValue === undefined)) {
      return true;
    }
    // For other cases, use the default comparison
    return undefined
  }

  ngOnInit(): void {
    if(this.guideService.isStartTour) {
      this.triggerForm.patchValue(dummyData.configBuilder.workflow.triggerArtifacts[0])
    }

    this.getEvents().then(() => {
      this.triggerForm.patchValue({ event: this.artifactData?.event || '' })
    })

    this.orgEventInitialization();
  }

  /**ngOnChanges */
  ngOnChanges() {
    this.configBuilderService.data$?.subscribe((res: any) => {
      if(res){
        this.triggerForm.patchValue({ repoOwner: res[0] })
        this.triggerForm?.controls['repoOwner'].disable();
      }
    })
  }

  /**
   * component initialization code
   */
  setInitialFrom() {
    this.buildForm();
    this.setListeners();
    this.triggerForm.patchValue({ event: this.artifactData?.event || '' })
    this.orgEventInitialization();
    if (this.artifactData?.action && this.artifactData?.action?.length > 0) {
      this.getActionApi(this.artifactData?.event).then(() => {
        this.actionInitialization();
        const selectedActionIds = this.artifactData?.action || [];

        // Map each action ID to its corresponding action from this.actions
        const selectedActions = selectedActionIds.map((actionId: any) => {
          return this.actions.find((act) => act.id === actionId);
        });
        this.triggerForm.patchValue({
          action: selectedActions.map((selectedAction: any) => selectedAction?.id)
        });
        this.allSelected = (selectedActions.length === this.actions.length)
        this.patchActionValues([this.actions[0].id])
      })
    }

    if (this.artifactData?.event) {
      this.getPayloadApi(this.artifactData?.event)
    }

    if (this._mode === 'read') {
      this.triggerForm.disable();
    } else if (this._mode === 'edit') {
      this.triggerForm?.controls['repoOwner'].disable();
    }
  }

  /**
   * builds trigger form
   */
  buildForm() {
    this.triggerForm = this.fb.group({
      repoPtrn: [this.artifactData?.repoPtrn || '', [Validators.required, this.customValidator('repoPtrn')]],
      branch: [this.artifactData?.branch || '', [Validators.required, this.customValidator('branch')]],
      artifact: this.fb.group({
        inputArtifact: [this.artifactData?.artifact.inputArtifact || ''],
        inputArtifactRepo: [this.artifactData?.artifact.inputArtifactRepo || '', [Validators.pattern(this.constant.REPO_URL_REGEX)]],
        outputArtifact: [this.artifactData?.artifact.outputArtifact || ''],
        outputArtifactRepo: [this.artifactData?.artifact.outputArtifactRepo || '', [Validators.pattern(this.constant.REPO_URL_REGEX)]],
      }),
      event: [this.artifactData?.event || '', [Validators.required]],
      action: [{ value: [], disabled: true }, [Validators.required]],
      repoOwner: [this.artifactData?.repoOwner || '', [Validators.required]],
    });

    this.statusChange.emit(this.triggerForm.valid);
  }

  //custome validator for branch 
  customValidator(identifier?: string): (control: AbstractControl) => { [key: string]: any } | null {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control?.value) {
        try {
          if (identifier === 'repoPtrn') {
            if (new RegExp(control.value)) {
              if (control.value.includes('https') && this.constant.REPO_URL_REGEX.test(control.value)) {
                return null
              } else if (!control.value.includes('https') && new RegExp(control.value)) {
                return null
              } else {
                return { isValidRegex: true }
              }
            }
          }
          new RegExp(control.value)
          return null
        } catch (err) {
          return { isValidRegex: true }
        }
      }
      return null;
    }
  }

  /**
   * sets event listeners
   */
  setListeners() {
    this.triggerForm.statusChanges.subscribe(() => this.statusChange.emit(this.triggerForm.valid));
  }

  /**
   * form value will be assigned to _artifactData
   */
  saveData() {
    this._artifactData = this.formValue;
  }

  /**
   * OnEventSection will trigger and populate the actions dropdown accordingly
   * @param event 
   */
  onEventSelection(event: any): void {
    this.getActions(event);
    this.getPayloadApi(event);
    this.isChangeEvent.emit(true);
    this.changeEventValue.emit(event)
  }

  /**selectAll function is used to select all or unselect all actions */
  toggleAllSelection() {
    if (this.allSelected) {
      this.select.options.forEach((item: MatOption) => item.select());
    } else {
      this.select.options.forEach((item: MatOption) => item.deselect());
    }
  }

  /**option click */
  optionClick() {
    let newStatus = true;
    this.select.options.forEach((item: MatOption) => {
      if (!item.selected) {
        newStatus = false;
      }
    });
    this.allSelected = newStatus;
  }

  /**filterOrganization is used to search and select any organization */
  filterOrganizations(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.organizations.filter(org => org.toLowerCase().includes(filterValue));
  }

  /**filterEvent is used to search and select any github events */
  private _filterEvents(value: any): any[] {
    const filterValue = (value || '').toLowerCase();
    return this.githubEvents.filter(event => event.name.toLowerCase().includes(filterValue));
  }

  /**onInput focus is used to display all the values on click of mat-autocomplete input */
  onInputFocus(val: any) {
    if (val === 'org') {
      this.filteredOrganizations = this.organizations;
    } else if (val === 'actions') {
      this.filterControl.setValue('');
    }
  }

  /**onPanelClose is used in action dropdown */
  onPanelClose() {
    this.filterControl.setValue('');
  }

  /**onEventPanelClose is used in event dropdown */
  onEventPanelClose() {
    this.eventFilterControl.setValue('');
  }

  /**organization and event dropdown initialization */
  orgEventInitialization() {
    /**mat auto complete for organization and event */
    this.triggerForm.get('repoOwner')?.valueChanges.subscribe((value) => {
      this.filteredOrganizations = this.filterOrganizations(value);
    });

    this.filteredEventOptions = this.eventFilterControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        return this._filterEvents(value);
      })
    );
  }

  /**actionDropdown initialization */
  actionInitialization() {
    this.filteredActions = this.filterControl.valueChanges.pipe(
      startWith(''),
      map((value: string) => {
        return this._filterActions(value);
      })
    );
  }
  /**
   * 
   * @param value pass search value
   * @returns returns the filtered array
   */
  _filterActions(value: any) {
    const filterValue = value.toLowerCase();
    const selectedValues = this.triggerForm.controls['action'].value;

    if (selectedValues && selectedValues.length > 0) {
      const filteredList = this.actions.filter(action => action.name.toLowerCase().includes(filterValue));
      const selectedItems = this.actions.filter(action => selectedValues.includes(action.id) && !filteredList.some(filteredAction => filteredAction.id === action.id));
      this.allSelected = this.actions.length === selectedItems.length;
      return [...filteredList, ...selectedItems];
    } else {
      return this.actions.filter(action => action.name.toLowerCase().includes(filterValue));
    }
  }

  /** This function call will get us all the github events present in the database */
  async getEvents() {
    let resp: any = await this.configBuilderService.getAllEvents().toPromise()
    this.githubEvents = resp?.data?.dbResp || [];
    this.eventFilterControl.setValue('');
  }

  /**getActions function is used call the api and assign the value to the dropdown */
  async getActions(id: number) {
    this.triggerForm.get('action')?.reset();
    this.allSelected = false;
    await this.getActionApi(id)
    this.triggerForm.get('action')?.enable();
    if(this.triggerForm.get('event')?.value) {
      this.patchActionValues([this.actions[0].id])
    }
    this.actionInitialization();
  }

  /**patch action value according to the event */
  patchActionValues(actionVal: any) {
    if (this.githubEvents.length == 0) {
      this.triggerForm.get('action')?.disable()
    } else if (this._mode == 'read') {
      this.triggerForm.get('action')?.disable()
    } else {
      this.triggerForm.get('action')?.enable()
    }

    let length = this.actions.length;

    if (length == 1 && this.actions[0].val == 'N/A') {
      this.triggerForm.get('action')?.patchValue(actionVal);
      this.triggerForm.get('action')?.disable();
    }
  }

  /**getActionApi function is the api call for selected events whatever actions are present will be fetched */
  async getActionApi(id: number) {
    let resp: any = await this.configBuilderService.getActions(id).toPromise()
    this.actions = resp.data.dbResp || [];
    let length = this.actions.length;

    if (length == 1 && this.actions[0].name == 'N/A') {
      this.actions = this.actions.map((el: any) => { el = { ...el, val: 'N/A', name: 'No actions available for the selected event' }; return el; })
    }

  }

  //api to get the payload object by event id
  async getPayloadApi(eventId: number) {
    let resp: any = await this.dpService.getPayload(eventId).toPromise()
    this.dpService.loadData(resp.data.dbResp || {})
  }


}
